import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, RefreshCw, Briefcase, Users,
  Clock, Eye, CheckCircle, XCircle, CalendarCheck,
  TrendingUp, ChevronDown, ChevronUp, Building2, MapPin
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { toast } from 'sonner';

const STATUS_FILTERS = [
  { key: '', label: 'Todas' },
  { key: 'active', label: 'Activas' },
  { key: 'paused', label: 'Pausadas' },
  { key: 'closed', label: 'Encerradas' },
  { key: 'draft', label: 'Rascunho' },
];

const JOB_STATUS_LABEL = {
  active:  { label: 'Activa',     cls: 'bg-emerald-100 text-emerald-700' },
  paused:  { label: 'Pausada',    cls: 'bg-amber-100 text-amber-700' },
  closed:  { label: 'Encerrada',  cls: 'bg-gray-200 text-gray-600' },
  draft:   { label: 'Rascunho',   cls: 'bg-blue-100 text-blue-700' },
};

function StatPill({ icon: Icon, label, value, color }) {
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${color}`}>
      <Icon className="h-3 w-3" />
      <span>{value}</span>
      <span className="font-normal opacity-75">{label}</span>
    </div>
  );
}

function ProgressBar({ pending, reviewed, accepted, rejected, total }) {
  if (!total) return <div className="h-1.5 bg-gray-100 rounded-full mt-2" />;
  const pct = (n) => `${Math.round((n / total) * 100)}%`;
  return (
    <div className="flex h-2 rounded-full overflow-hidden mt-2 gap-px">
      {pending  > 0 && <div className="bg-blue-400"   title={`Por rever: ${pending}`}   style={{ width: pct(pending) }} />}
      {reviewed > 0 && <div className="bg-amber-400"  title={`Vistos: ${reviewed}`}     style={{ width: pct(reviewed) }} />}
      {accepted > 0 && <div className="bg-emerald-500" title={`Aceites: ${accepted}`}   style={{ width: pct(accepted) }} />}
      {rejected > 0 && <div className="bg-rose-400"   title={`Recusados: ${rejected}`} style={{ width: pct(rejected) }} />}
    </div>
  );
}

function JobRow({ job }) {
  const [open, setOpen] = useState(false);
  const { stats } = job;
  const statusCfg = JOB_STATUS_LABEL[job.status] || { label: job.status, cls: 'bg-gray-100 text-gray-500' };

  const convRate = stats.total > 0
    ? Math.round(((stats.accepted + stats.interviews) / stats.total) * 100)
    : 0;

  return (
    <Card className={`overflow-hidden transition-shadow hover:shadow-md ${open ? 'shadow-md' : ''}`}
      data-testid={`job-overview-${job.id}`}>
      {/* Header row */}
      <div
        className="flex items-start gap-3 p-4 cursor-pointer"
        onClick={() => setOpen(o => !o)}>

        {/* Icon */}
        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
          <Briefcase className="h-5 w-5 text-blue-600" />
        </div>

        {/* Job info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            <span className="font-bold text-gray-900 text-sm">{job.title}</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusCfg.cls}`}>
              {statusCfg.label}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-3 text-xs text-gray-400 mb-1">
            {job.company !== '—' && (
              <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{job.company}</span>
            )}
            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{job.location}</span>
            <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{job.category}</span>
          </div>

          {/* Mini stats */}
          <div className="flex flex-wrap gap-1.5">
            <StatPill icon={Users}        label="candidaturas" value={stats.total}     color="bg-blue-50 text-blue-600" />
            {stats.interviews > 0 && <StatPill icon={CalendarCheck} label="entrevistas"   value={stats.interviews} color="bg-violet-50 text-violet-600" />}
            {stats.accepted   > 0 && <StatPill icon={CheckCircle}   label="aceites"       value={stats.accepted}   color="bg-emerald-50 text-emerald-600" />}
            {stats.rejected   > 0 && <StatPill icon={XCircle}       label="recusados"     value={stats.rejected}   color="bg-rose-50 text-rose-600" />}
          </div>

          <ProgressBar {...stats} total={stats.total} />
        </div>

        {/* Chevron */}
        <div className="flex-shrink-0 mt-1 text-gray-400">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </div>

      {/* Expanded detail */}
      {open && (
        <div className="border-t border-gray-100 bg-slate-50 px-4 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
            {[
              { label: 'Total candidaturas', value: stats.total,     color: 'text-blue-700',    bg: 'bg-blue-50',    icon: Users },
              { label: 'Por rever',          value: stats.pending,   color: 'text-blue-600',    bg: 'bg-blue-50',    icon: Clock },
              { label: 'Vistos',             value: stats.reviewed,  color: 'text-amber-700',   bg: 'bg-amber-50',   icon: Eye },
              { label: 'Aceites',            value: stats.accepted,  color: 'text-emerald-700', bg: 'bg-emerald-50', icon: CheckCircle },
              { label: 'Recusados',          value: stats.rejected,  color: 'text-rose-700',    bg: 'bg-rose-50',    icon: XCircle },
            ].map(({ label, value, color, bg, icon: Icon }) => (
              <div key={label} className={`${bg} rounded-xl p-3 text-center`}>
                <Icon className={`h-4 w-4 mx-auto mb-1 ${color}`} />
                <div className={`text-2xl font-black ${color}`}>{value}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">{label}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            {stats.interviews > 0 && (
              <div className="flex items-center gap-1.5 bg-violet-50 rounded-lg px-3 py-2">
                <CalendarCheck className="h-4 w-4 text-violet-600" />
                <span className="font-semibold text-violet-700">{stats.interviews}</span>
                <span>entrevista{stats.interviews !== 1 ? 's' : ''} agendada{stats.interviews !== 1 ? 's' : ''}</span>
              </div>
            )}
            {stats.total > 0 && (
              <div className="flex items-center gap-1.5 bg-gray-100 rounded-lg px-3 py-2">
                <TrendingUp className="h-4 w-4 text-gray-500" />
                <span>Taxa de progressão:</span>
                <span className="font-bold text-gray-700">{convRate}%</span>
              </div>
            )}
            {job.created_at && (
              <div className="flex items-center gap-1.5 text-gray-400">
                <span>Publicada em {new Date(job.created_at).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
              </div>
            )}
          </div>

          {stats.total === 0 && (
            <p className="text-xs text-gray-400 italic mt-2">Nenhuma candidatura recebida ainda.</p>
          )}
        </div>
      )}
    </Card>
  );
}

export default function AdminJobsOverview() {
  const navigate = useNavigate();
  const [data, setData]       = useState({ jobs: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [statusF, setStatusF] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusF) params.set('status', statusF);
      if (search.trim()) params.set('q', search.trim());
      const { data: d } = await api.get(`/admin/jobs/overview?${params}`);
      setData(d);
    } catch {
      toast.error('Erro ao carregar vagas.');
    } finally {
      setLoading(false);
    }
  }, [statusF, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 400 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const { jobs, totals } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">Desempenho das Vagas</h1>
            <p className="text-sm text-gray-500">Candidaturas por vaga — sem identificar candidatos</p>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
        </div>

        {/* Totais globais */}
        {totals.jobs > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
            {[
              { label: 'Vagas',         value: totals.jobs,         color: 'text-gray-800',    bg: 'bg-white' },
              { label: 'Candidaturas',  value: totals.applications, color: 'text-blue-700',    bg: 'bg-blue-50' },
              { label: 'Por rever',     value: totals.pending,      color: 'text-blue-600',    bg: 'bg-blue-50' },
              { label: 'Vistos',        value: totals.reviewed,     color: 'text-amber-700',   bg: 'bg-amber-50' },
              { label: 'Aceites',       value: totals.accepted,     color: 'text-emerald-700', bg: 'bg-emerald-50' },
              { label: 'Recusados',     value: totals.rejected,     color: 'text-rose-700',    bg: 'bg-rose-50' },
            ].map(({ label, value, color, bg }) => (
              <Card key={label} className={`p-3 text-center ${bg}`}>
                <div className={`text-xl font-black ${color}`}>{value ?? 0}</div>
                <div className="text-[10px] text-gray-400 mt-0.5">{label}</div>
              </Card>
            ))}
          </div>
        )}

        {/* Pesquisa */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            placeholder="Pesquisar vaga, empresa ou localização…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="jobs-overview-search"
          />
        </div>

        {/* Filtros de estado */}
        <div className="flex flex-wrap gap-2 mb-5">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setStatusF(f.key)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all
                ${statusF === f.key
                  ? 'border-blue-400 bg-blue-50 text-blue-700 shadow-sm'
                  : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`}
              data-testid={`status-filter-${f.key || 'all'}`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Lista */}
        {loading && (
          <div className="text-center py-16 text-gray-400">
            <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin opacity-40" />
            <p>A carregar vagas…</p>
          </div>
        )}

        {!loading && jobs.length === 0 && (
          <Card className="p-12 text-center text-gray-400">
            <Briefcase className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">Nenhuma vaga encontrada</p>
          </Card>
        )}

        {!loading && jobs.length > 0 && (
          <div className="space-y-3">
            {jobs.map(j => <JobRow key={j.id} job={j} />)}
          </div>
        )}
      </div>
    </div>
  );
}
